import type { User } from '@lmc/ts-common'
import { Avatar } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Popover } from 'antd'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { capitalize } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type ClockIn, RotaInstanceStatus } from 'screens/Rostering/types'
import styled from 'styled-components'
import DeactivatedIcon from '../icons/DeactivatedIcon'
import DeletedIcon from '../icons/DeletedIcon'
import QuestionOutlined from '../icons/QuestionOutlined'

const AllocationPopover = ({
  original,
  hasAllocatedTeamMembers,
  children,
  missingAllocationsNumber,
  setAllocationWindowVisible,
  setDrawerVisible,
  isOpen,
  setIsOpen,
  disabled
}: any) => {
  const { t } = useTranslation()
  const { teamMembers } = original

  const flags = useFeatureFlag({
    flagNames: ['rostering_change_management']
  })?.data

  const flagsRosteringChangeManagementEnabled =
    flags?.rostering_change_management?.enabled

  const clockInStatusMap = original.clockIns.reduce(
    (map: Map<string, string>, clockIn: ClockIn) => {
      map.set(clockIn.teamMemberId, clockIn.status)
      return map
    },
    new Map()
  )

  const handleOpenChange = (newOpen: boolean) => {
    if (!disabled) {
      setIsOpen(newOpen)
    }
  }

  const handleMissingAllocationClick = (e: any) => {
    e.stopPropagation()
    e.preventDefault()
    setAllocationWindowVisible(true)
    setIsOpen(false)
  }

  const handleTeamMemberClick = (e: any) => {
    e.stopPropagation()
    e.preventDefault()
    setDrawerVisible(true)
    setIsOpen(false)
  }

  const getTeamMemberStatus = (teamMemberId: string) => {
    let status = RotaInstanceStatus.PENDING

    if (original.clockIns.length && clockInStatusMap.has(teamMemberId)) {
      status = clockInStatusMap.get(teamMemberId)
    }

    return capitalize(status)
  }

  return (
    <Popover
      placement="bottom"
      showArrow
      arrow={{ pointAtCenter: true }}
      trigger={disabled ? [] : 'hover'}
      open={isOpen}
      onOpenChange={handleOpenChange}
      content={
        <ContentContainer onClick={() => setIsOpen(!isOpen)}>
          <TitleText level={2}>{t('TEAM_MEMBERS')}</TitleText>
          {hasAllocatedTeamMembers &&
            teamMembers.map((teamMember: { carer: User }) => (
              <PersonRow
                key={teamMember?.carer?.id}
                onClick={(e) => handleTeamMemberClick(e)}
              >
                <LeftContainer>
                  <StyledAvatar
                    size={16}
                    url={teamMember?.carer?.picture as unknown as string}
                  />
                  <PersonText weight="medium">
                    {teamMember?.carer?.name?.first}
                  </PersonText>
                  <PersonText weight="medium">
                    {teamMember?.carer?.name?.last}
                  </PersonText>
                  {flagsRosteringChangeManagementEnabled &&
                    !teamMember?.carer?.active &&
                    !teamMember?.carer?.deleted && (
                      <IconContainer>
                        <DeactivatedIcon />
                      </IconContainer>
                    )}
                  {flagsRosteringChangeManagementEnabled &&
                    teamMember?.carer?.deleted && (
                      <IconContainer>
                        <DeletedIcon />
                      </IconContainer>
                    )}
                </LeftContainer>
                <PersonText level={2}>
                  {getTeamMemberStatus(teamMember?.carer?.id)}
                </PersonText>
              </PersonRow>
            ))}
          {missingAllocationsNumber > 0 &&
            Array.from({ length: missingAllocationsNumber }).map((_, i) => (
              <PersonRow
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                onClick={(e) => handleMissingAllocationClick(e)}
              >
                <LeftContainer>
                  <StyledQuestionOutlined />
                  <MissingText weight="medium">
                    {t('MISSING_ALLOCATION')}
                  </MissingText>
                </LeftContainer>
              </PersonRow>
            ))}
        </ContentContainer>
      }
    >
      {children}
    </Popover>
  )
}

const ContentContainer = styled.div`
  min-width: 232px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const PersonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.color.neutral.color9};
  border-radius: 8px;
  padding: 16px;
  gap: 4px;

  &:hover {
    cursor: pointer;
  }
`

const StyledAvatar = styled(Avatar)`
  border: 0.5px solid ${(props) => props.theme.color.neutral.color7};
`

const PersonText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const MissingText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const TitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`
const StyledQuestionOutlined = styled(QuestionOutlined)`
  color: ${({ theme }) => theme.color.status.warning};
  width: 16px;
  height: 16px;
`
const IconContainer = styled.div`
  color: ${(props) => props.theme.color.neutral.color5};
  width: 15px;
  height: 15px;
`

export default AllocationPopover
